import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Clock, MoreVertical, Check, Eye, RotateCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { UnifiedNotification } from '@/hooks/useFleetNotifications';

interface NotificationItemProps {
  notification: UnifiedNotification;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const Icon = notification.icon;
  
  return (
    <Card className={cn(
      "p-4 mb-3 transition-colors",
      !notification.isRead && "bg-blue-50 dark:bg-blue-950/20 border-blue-200"
    )}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          "p-2 rounded-lg",
          notification.priority === 'high' ? "bg-destructive/10 text-destructive" :
          notification.priority === 'medium' ? "bg-yellow-100 text-yellow-600 dark:bg-yellow-950/30 dark:text-yellow-500" :
          "bg-blue-100 text-blue-600 dark:bg-blue-950/30 dark:text-blue-500"
        )}>
          <Icon className="h-5 w-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <h3 className="font-semibold truncate">{notification.title}</h3>
            {notification.badge && (
              <Badge variant={notification.badge.variant}>
                {notification.badge.label}
              </Badge>
            )}
          </div>
          
          <p className="text-sm text-muted-foreground mb-2">
            {notification.description}
          </p>
          
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(notification.timestamp), {
                addSuffix: true,
                locale: ptBR,
              })}
            </span>
            
            {!notification.isRead && (
              <Badge variant="outline" className="text-xs">
                <Eye className="h-3 w-3 mr-1" />
                Não lida
              </Badge>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!notification.isRead && (
              <DropdownMenuItem>
                <Check className="h-4 w-4 mr-2" />
                Marcar como lida
              </DropdownMenuItem>
            )}
            <DropdownMenuItem>
              <Eye className="h-4 w-4 mr-2" />
              Ver detalhes
            </DropdownMenuItem>
            {notification.type === 'gps' && notification.metadata?.status === 'failed' && (
              <DropdownMenuItem>
                <RotateCw className="h-4 w-4 mr-2" />
                Tentar reenviar
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}
